import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMe } from './api/hooks';
import { OfflineNotice } from './components/OfflineNotice';
import { Spinner } from './components/Spinner';
import LoginPage from './pages/LoginPage';
import NotePage from './pages/NotePage';
import NotesPage from './pages/NotesPage';
import RemindersPage from './pages/RemindersPage';
import TodosPage from './pages/TodosPage';

/**
 * The authentication guard, around every screen but `/login`.
 *
 * The server already refuses every protected route on its own — this guard adds
 * no security. What it adds is the difference between a page that renders and
 * then empties itself, and one that waits: while `GET /api/auth/me` is in flight
 * there is no honest answer to give, so it shows the spinner rather than
 * guessing.
 */
function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const { data: user, isPending, fetchStatus } = useMe();
  const location = useLocation();

  // Offline: the question was never answered rather than answered with a refusal.
  // Redirecting would land on a sign-in screen that needs the same network, and
  // waiting would be a spinner with no end — which is what somebody opening the
  // application from a home screen in a tunnel would otherwise meet. Only
  // `navigator.onLine === false` is trusted, since that is the direction of the
  // flag that means something.
  if (!user && (fetchStatus === 'paused' || !navigator.onLine)) {
    return (
      <div className="mx-auto flex h-full max-w-md items-center px-5">
        <OfflineNotice />
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Carry the destination along: signing in should return to the page that was
  // asked for, not to the root, and a bookmark deep into the application is
  // exactly the case where being dropped at the front door is annoying.
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  return children;
}

export default function App(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* The root is not a screen. There was a home page naming the four modules
          until todos became real; the rail carries that now, and a screen whose
          only job was to point at the others would be a stop on the way to the
          one that was wanted (D260814k). */}
      <Route path="/" element={<Navigate to="/todos" replace />} />
      <Route
        path="/todos"
        element={
          <RequireAuth>
            <TodosPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reminders"
        element={
          <RequireAuth>
            <RemindersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/notes"
        element={
          <RequireAuth>
            <NotesPage />
          </RequireAuth>
        }
      />
      {/* The only screen in the application addressed by a row's identifier.
          A note is a document, so it has a URL worth keeping — and reloading it
          works because the server answers the shell for anything outside
          `/api/`. */}
      <Route
        path="/notes/:id"
        element={
          <RequireAuth>
            <NotePage />
          </RequireAuth>
        }
      />
      {/* Everything else leads to the todos rather than to a "not found" page:
          a stale bookmark or a mistyped path is better answered with a screen
          that exists, and the rail is one press away from the other one. */}
      <Route path="*" element={<Navigate to="/todos" replace />} />
    </Routes>
  );
}
